import { useState } from "react";
import { Title, TextInput, ActionIcon, Group } from "@mantine/core";
import { IconEdit, IconCheck } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { editRuleName } from "../../../../services/rules";
import { ruleQueryKey } from "../../../../components/QueryBuilder/hooks/queryKeys";
import { getAxiosResponseError } from "../../../../utils/functions";

/**
 * Click-to-edit rule name. Optimistically updates the cached rule on
 * save so the title doesn't flicker; rolls back on error. When
 * `canEdit` is false it renders as a static title.
 */
export default function RuleNameEdit({ ruleId, defaultName, canEdit = true }) {
  const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [inputValue, setInputValue] = useState(defaultName);

  const mutation = useMutation({
    mutationFn: editRuleName,
    onMutate: async (vars) => {
      const key = ruleQueryKey(ruleId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      if (previous) {
        queryClient.setQueryData(key, { ...previous, name: vars.name });
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(ruleQueryKey(ruleId), ctx.previous);
      }
      toast.error(getAxiosResponseError(err));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ruleQueryKey(ruleId) });
    },
  });

  if (!canEdit) {
    return (
      <Title order={4} fw={500}>
        {defaultName}
      </Title>
    );
  }

  const handleSave = () => {
    if (inputValue && inputValue !== defaultName) {
      mutation.mutate({ id: ruleId, name: inputValue });
    }
    setEditMode(false);
  };

  if (editMode) {
    return (
      <Group gap={4} align="center">
        <TextInput
          value={inputValue}
          autoFocus
          variant="unstyled"
          size="md"
          styles={{ input: { borderBottom: "1px solid var(--mantine-color-gray-4)", borderRadius: 0 } }}
          onChange={(ev) => setInputValue(ev.currentTarget.value)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") handleSave();
            if (ev.key === "Escape") setEditMode(false);
          }}
        />
        <ActionIcon c="green" variant="transparent" onClick={handleSave}>
          <IconCheck />
        </ActionIcon>
      </Group>
    );
  }

  return (
    <Group gap={4} align="center">
      <Title order={4} fw={500}>
        {defaultName}
      </Title>
      <ActionIcon c="gray" variant="transparent" onClick={() => setEditMode(true)}>
        <IconEdit size="20px" />
      </ActionIcon>
    </Group>
  );
}
